
<template>
  <div
    class="py-4 px-4 h-100"
  >
    <template>
      <section>
        <b-row>
          <b-col>
            <base-help-tooltip
              popover-placement="bottom"
            >
              <template v-slot:left>
                <div>
                  <h1 class="page-title">
                    Content Performance
                  </h1>
                </div>
              </template>
              <template v-slot:popover-body>
                <p
                  class="mb-0"
                >
                  A depiction of how each individual content item has delivered and performed during this campaign.
                </p>
              </template>
            </base-help-tooltip>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row class="mt-6 mb-3">
          <b-col>
            <ReportStatements
              :type="'CONTENT_PERFORMANCE'"
              :has-replacements="true"
              v-if="$route.name !== 'MultipleReportingIndex'"
            />
          </b-col>
        </b-row>
      </section>
      <section>
        <Filters
          @updateReport="updateReport"
          @setPageLoading="setPageLoading"
          @updateDates="updateDates"
          @areAllParametersPresent="areAllParametersPresent"
        />
      </section>
      <div
        v-if="isPageLoading"
        class="loader mx-auto h-100"
      />
      <div v-else>
        <section>
          <b-row>
            <b-col
              cols="12"
            >
              <b-card
                style="min-height: calc(10rem);"
              >
                <div
                  v-if="isContentStatsTableLoading"
                  class="loader mx-auto my-5"
                />
                <template
                  v-else
                >
                  <div
                    v-if="(contentStats.length || isContentStatsTableBusy) && allParametersArePresent"
                    class="table-responsive"
                  >
                    <b-table
                      class="card-table table-vcenter text-nowrap"
                      tbody-tr-class="content-performance-table-row"
                      :striped="true"
                      :bordered="false"
                      :hover="true"
                      :items="contentStats"
                      :fields="contentStatsHeaders"
                      :busy="isContentStatsTableBusy"
                      :sort-by.sync="sortTableBy"
                      data-test="content-performance-table"
                    >
                      <template
                        v-slot:cell(impressions)="{item}"
                      >
                        {{ toLocaleString(item.impressions) }}
                      </template>
                      <template
                        v-slot:cell(clicks)="{item}"
                      >
                        {{ toLocaleString(item.clicks) }}
                      </template>
                      <template
                        v-slot:cell(ctr)="{item}"
                      >
                        {{ toLocaleString(item.ctr, true) + "%" }}
                      </template>
                      <template
                        v-slot:cell(scrolls)="{item}"
                      >
                        {{ isValidNumber(item.scrolls) ? toLocaleString(item.scrolls) : "N/A" }}
                      </template>
                      <template
                        v-slot:cell(str)="{item}"
                      >
                        {{ isValidNumber(item.str) ? toLocaleString(item.str, true) + "%" : "N/A" }}
                      </template>
                      <template
                        #table-busy
                        style="min-height: calc(10rem);"
                      >
                        <div class="loader mx-auto" />
                      </template>
                    </b-table>
                  </div>
                  <b-alert
                    v-else
                    show
                    variant="warning"
                    class="w-50 mx-auto text-center"
                  >
                    There's no data to show
                  </b-alert>
                </template>
              </b-card>
            </b-col>
          </b-row>
        </section>
      </div>
    </template>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

import helper from '@/core/helper';
import ReportStatements from '@/modules/reporting/components/ReportStatements.vue';
import Filters from '@/modules/reporting/components/Filters.vue';
import numbersHelper from '@/core/mixins/numbersHelper';
import fileHelper from '@/core/mixins/fileHelper';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

export default {
  name: 'ContentPerformance',
  components: {
    Filters,
    BaseHelpTooltip,
    ReportStatements,
  },
  mixins: [
    numbersHelper,
    fileHelper,
  ],
  data() {
    return {
      isPageLoading: false,
      campaignList: [],
      campaignPublishersList: [],
      campaignContentItems: [],
      selectedCampaignIds: [],
      selectedPublishersIds: [],
      selectedCreativesIds: [],
      selectedContentItemIds: [],
      dateRange: {
        start: -1,
        end: -1,
      },
      isContentStatsTableLoading: false,
      contentStats: [],
      sortTableBy: 'title',
      isContentStatsTableBusy: false,
      allParametersArePresent: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_ORDERS_START_DATE,
      campaignTypes.GET_ORDERS_END_DATE,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ACTIVE_CREATIVES,
    ]),
    ...mapState('content', [
      'feedId',
    ]),
    hasScrollsData() {
      return this.contentStats.some((stats) => stats.scrolls != null);
    },
    contentStatsHeaders() {
      const headersWithScroll = [
        { key: 'title', sortable: true },
        { key: 'impressions', sortable: true },
        { key: 'clicks', sortable: true },
        { key: 'ctr', label: 'ctr%', sortable: true },
        { key: 'scrolls', sortable: true },
        { key: 'str', label: 'str%', sortable: true },
      ];
      const headersWithNoScroll = [
        { key: 'title', sortable: true },
        { key: 'impressions', sortable: true },
        { key: 'clicks', sortable: true },
        { key: 'ctr', label: 'ctr%', sortable: true },
      ];
      const rootElement = document.querySelector(':root');
      const width = this.hasScrollsData ? '10%' : '12%';
      rootElement.style.setProperty('--not-first-column-width', width);
      return this.hasScrollsData ? headersWithScroll : headersWithNoScroll;
    },
    startDateForReport() {
      return this.dateRange?.start == null || this.dateRange.start === -1
        ? this.GET_ORDERS_START_DATE : this.dateRange.start;
    },
    endDateForReport() {
      return this.dateRange?.end == null || this.dateRange.end === -1
        ? this.GET_ORDERS_END_DATE : this.dateRange.end;
    },
  },
  methods: {
    ...mapActions('reporting', [
      reportingTypes.FETCH_CAMPAIGN_CONTENT_DAILY_STATS,
    ]),
    updateDates(startDate, endDate) {
      this.dateRange.start = startDate;
      this.dateRange.end = endDate;
      this.updateReport();
    },
    updateReport: helper.debounce(function fetchData(data) {
      if (this.allParametersArePresent) {
        this.selectedCampaignIds = data.campaignIds;
        this.selectedPublishersIds = data.publisherIds;
        this.selectedCreativesIds = data.creativeIds;
        this.selectedContentItemIds = data.contentItemIds;
        this.fetchContentData();
      }
    }, 1500),
    fetchContentData() {
      this.isContentStatsTableBusy = true;
      const { advertiserId } = this.$route.params;
      const payload = {
        advertiserId,
        body: {
          fromDate: this.startDateForReport,
          toDate: this.endDateForReport,
          campaignIds: this.selectedCampaignIds,
          publisherIds: this.selectedPublishersIds,
          creativeIds: this.selectedCreativesIds,
          contentItemIds: this.selectedContentItemIds,
        },
      };

      this.FETCH_CAMPAIGN_CONTENT_DAILY_STATS(payload)
        .then((response) => {
          if (this.contentStats.length) {
            return this.contentStats.splice(0, this.contentStats.length, ...response.contentStats);
          }
          this.contentStats = response.contentStats;
        })
        .finally(() => {
          this.isContentStatsTableBusy = false;
          this.isContentStatsTableLoading = false;
        });
    },
    setPageLoading(value) {
      this.isPageLoading = value;
    },
    areAllParametersPresent(value) {
      this.allParametersArePresent = value;
    },
  },
};
</script>
<style scoped lang="scss">
$not-first-column-width: 10%;
::v-deep {
  .card-table thead tr th:not(:first-child),
  .card-table tbody tr td:not(:first-child) {
    width: var(--not-first-column-width, $not-first-column-width);
  }
  .table.b-table > thead > tr > [aria-sort] {
    background-position: right 0.5rem center;
  }
  .card-table thead tr th:not(:last-child),
  .card-table tbody tr td:not(:last-child) {
    border-right: 1px solid rgb(222, 226, 230);
  }
}
</style>
